/**
 * Help Panel Component
 *
 * Slide-out panel that displays context-aware help or browse mode.
 * Renders markdown content with proper styling.
 */

import React from 'react';
import ReactMarkdown from 'react-markdown';
import { BookOpen, HelpCircle, Lightbulb, AlertTriangle, FileText, Crosshair, LayoutGrid } from 'lucide-react';
import { useHelpContext } from './HelpContext';
import { DocumentationDoc, DocumentationByCategory } from '../../types/documentation.types';

// Get icon based on document category
function getCategoryIcon(category: string) {
  const iconProps = { size: 18, strokeWidth: 1.5 };
  switch (category) {
    case 'overview':
      return <BookOpen {...iconProps} style={{ color: 'var(--pm-cyan-text)' }} />;
    case 'status-help':
      return <HelpCircle {...iconProps} style={{ color: 'var(--pm-help-icon-purple)' }} />;
    case 'how-to':
      return <Lightbulb {...iconProps} style={{ color: 'var(--pm-help-icon-yellow)' }} />;
    case 'troubleshooting':
      return <AlertTriangle {...iconProps} style={{ color: 'var(--pm-help-icon-red)' }} />;
    case 'reference':
      return <FileText {...iconProps} style={{ color: 'var(--pm-help-icon-green)' }} />;
    default:
      return <FileText {...iconProps} style={{ color: 'var(--pm-text-muted)' }} />;
  }
}

// Styled markdown renderer using react-markdown
function MarkdownContent({ content }: { content: string }) {
  return (
    <ReactMarkdown
      components={{
        h1: ({ children }) => (
          <h1 style={{ fontSize: '1.5em', fontWeight: '600', marginTop: '20px', marginBottom: '12px' }}>
            {children}
          </h1>
        ),
        h2: ({ children }) => (
          <h2 style={{ fontSize: '1.2em', fontWeight: '600', marginTop: '28px', marginBottom: '12px', color: 'var(--pm-cyan-text)', letterSpacing: '0.01em' }}>
            {children}
          </h2>
        ),
        h3: ({ children }) => (
          <h3 style={{ fontSize: '1em', fontWeight: '600', marginTop: '20px', marginBottom: '8px', color: 'var(--pm-text-primary)' }}>
            {children}
          </h3>
        ),
        p: ({ children }) => (
          <p style={{ margin: '8px 0', lineHeight: '1.6' }}>{children}</p>
        ),
        ul: ({ children }) => (
          <ul style={{ margin: '12px 0', paddingLeft: '24px', listStyleType: 'disc' }}>{children}</ul>
        ),
        ol: ({ children }) => (
          <ol style={{ margin: '12px 0', paddingLeft: '24px', listStyleType: 'decimal' }}>{children}</ol>
        ),
        li: ({ children }) => (
          <li style={{ marginBottom: '4px' }}>{children}</li>
        ),
        strong: ({ children }) => <strong style={{ fontWeight: 600 }}>{children}</strong>,
        em: ({ children }) => <em>{children}</em>,
        code: ({ children, className }) => {
          // Check if it's a code block (has language class) or inline
          const isBlock = className?.startsWith('language-');
          if (isBlock) {
            return <code>{children}</code>;
          }
          return (
            <code
              style={{
                background: 'var(--pm-surface-4)',
                padding: '2px 6px',
                borderRadius: '4px',
                fontSize: '0.9em',
              }}
            >
              {children}
            </code>
          );
        },
        pre: ({ children }) => (
          <pre
            style={{
              background: 'var(--pm-shadow)',
              padding: '12px',
              borderRadius: '8px',
              overflow: 'auto',
              margin: '12px 0',
              fontSize: '0.9em',
            }}
          >
            {children}
          </pre>
        ),
      }}
    >
      {content}
    </ReactMarkdown>
  );
}

// Document card for list view
function DocCard({
  doc,
  onClick,
}: {
  doc: DocumentationDoc;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        width: '100%',
        textAlign: 'left',
        background: 'var(--pm-bg-card-alt)',
        border: '1px solid var(--pm-border-default)',
        borderRadius: '8px',
        padding: '14px 16px',
        cursor: 'pointer',
        transition: 'all 0.15s ease',
        marginBottom: '10px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'var(--pm-border-default)';
        e.currentTarget.style.borderColor = 'var(--pm-cyan-border-hover)';
        e.currentTarget.style.transform = 'translateY(-1px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'var(--pm-bg-card-alt)';
        e.currentTarget.style.borderColor = 'var(--pm-border-default)';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      <div style={{ flexShrink: 0, marginTop: '2px' }}>
        {getCategoryIcon(doc.category)}
      </div>
      <div>
        <div style={{ fontWeight: '500', marginBottom: '4px', color: 'var(--pm-text-primary)' }}>{doc.title}</div>
        {doc.subtitle && (
          <div style={{ fontSize: '0.85em', color: 'var(--pm-text-muted)' }}>{doc.subtitle}</div>
        )}
      </div>
    </button>
  );
}

// Category section for browse mode
function CategorySection({
  category,
  onSelectDoc,
}: {
  category: DocumentationByCategory;
  onSelectDoc: (doc: DocumentationDoc) => void;
}) {
  return (
    <div style={{ marginBottom: '24px' }}>
      <h3
        style={{
          fontSize: '0.75em',
          fontWeight: '600',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: 'hsl(var(--muted-foreground))',
          marginBottom: '12px',
        }}
      >
        {category.label}
      </h3>
      {category.docs.map((doc) => (
        <DocCard key={doc.id} doc={doc} onClick={() => onSelectDoc(doc)} />
      ))}
    </div>
  );
}

export function HelpPanel() {
  const {
    isOpen,
    closePanel,
    mode,
    setMode,
    contextDocs,
    browseDocs,
    isLoading,
    selectedDoc,
    setSelectedDoc,
    currentContext,
  } = useHelpContext();

  if (!isOpen) {
    return null;
  }

  const hasContextDocs = contextDocs.length > 0;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={closePanel}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'var(--pm-bg-overlay)',
          zIndex: 9998,
          animation: 'help-backdrop-fade 0.2s ease-out',
        }}
      />

      {/* Panel */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: '420px',
          maxWidth: '100vw',
          background: 'var(--pm-bg-card)',
          borderLeft: '1px solid var(--pm-border-default)',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          color: 'var(--pm-text-primary)',
          boxShadow: '-4px 0 20px var(--pm-bg-overlay)',
          animation: 'help-panel-slide 0.25s ease-out',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid var(--pm-border-default)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {selectedDoc ? (
            <button
              onClick={() => setSelectedDoc(null)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: 'none',
                border: 'none',
                color: 'hsl(var(--primary))',
                cursor: 'pointer',
                fontSize: '0.9em',
                padding: 0,
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
              Back
            </button>
          ) : (
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => setMode('context')}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: 'none',
                  background: mode === 'context' ? 'hsl(var(--primary))' : 'hsl(var(--secondary))',
                  color: mode === 'context' ? 'hsl(var(--primary-foreground))' : 'hsl(var(--foreground))',
                  cursor: 'pointer',
                  fontWeight: '500',
                  fontSize: '0.85em',
                  transition: 'background 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <Crosshair size={14} />
                Context Help
              </button>
              <button
                onClick={() => setMode('browse')}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: 'none',
                  background: mode === 'browse' ? 'hsl(var(--primary))' : 'hsl(var(--secondary))',
                  color: mode === 'browse' ? 'hsl(var(--primary-foreground))' : 'hsl(var(--foreground))',
                  cursor: 'pointer',
                  fontWeight: '500',
                  fontSize: '0.85em',
                  transition: 'background 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <LayoutGrid size={14} />
                Browse All
              </button>
            </div>
          )}

          <button
            onClick={closePanel}
            aria-label="Close help panel"
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '6px',
              border: 'none',
              background: 'hsl(var(--secondary))',
              color: 'hsl(var(--foreground))',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'hsl(var(--accent))';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'hsl(var(--secondary))';
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div
          style={{
            flex: 1,
            overflow: 'auto',
            padding: '20px',
          }}
        >
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: 'hsl(var(--muted-foreground))' }}>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                style={{ animation: 'help-spin 1s linear infinite', margin: '0 auto' }}
              >
                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
              </svg>
              <p style={{ marginTop: '12px' }}>Loading...</p>
            </div>
          ) : selectedDoc ? (
            /* Detail view */
            <div>
              <h1 style={{ fontSize: '1.5em', fontWeight: '600', marginBottom: '8px' }}>{selectedDoc.title}</h1>
              {selectedDoc.subtitle && (
                <p style={{ color: 'var(--pm-text-muted)', marginBottom: '16px' }}>{selectedDoc.subtitle}</p>
              )}
              <hr style={{ border: 'none', borderTop: '1px solid var(--pm-border-default)', marginBottom: '20px' }} />
              <div style={{ lineHeight: '1.6' }}><MarkdownContent content={selectedDoc.content} /></div>
            </div>
          ) : mode === 'context' ? (
            /* Context mode */
            hasContextDocs ? (
              <div>
                {contextDocs.length === 1 ? (
                  /* Single doc - show content directly */
                  <div>
                    <h1 style={{ fontSize: '1.5em', fontWeight: '600', marginBottom: '8px' }}>{contextDocs[0].title}</h1>
                    {contextDocs[0].subtitle && (
                      <p style={{ color: 'var(--pm-text-muted)', marginBottom: '16px' }}>{contextDocs[0].subtitle}</p>
                    )}
                    <hr style={{ border: 'none', borderTop: '1px solid var(--pm-border-default)', marginBottom: '20px' }} />
                    <div style={{ lineHeight: '1.6' }}><MarkdownContent content={contextDocs[0].content} /></div>
                  </div>
                ) : (
                  /* Multiple docs - show list */
                  <div>
                    <p style={{ color: 'hsl(var(--muted-foreground))', marginBottom: '16px' }}>
                      {contextDocs.length} help articles for this context
                    </p>
                    {contextDocs.map((doc) => (
                      <DocCard key={doc.id} doc={doc} onClick={() => setSelectedDoc(doc)} />
                    ))}
                  </div>
                )}
              </div>
            ) : (
              /* No context docs */
              <div style={{ textAlign: 'center', padding: '40px 0', color: 'hsl(var(--muted-foreground))' }}>
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  style={{ margin: '0 auto 16px' }}
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                  <path d="M12 17h.01" />
                </svg>
                <p style={{ marginBottom: '8px' }}>No help available for this context</p>
                <p style={{ fontSize: '0.85em' }}>
                  Try{' '}
                  <button
                    onClick={() => setMode('browse')}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'hsl(var(--primary))',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                    }}
                  >
                    browsing all help articles
                  </button>
                </p>
                {currentContext && (
                  <p style={{ fontSize: '0.75em', marginTop: '16px', color: 'hsl(var(--muted-foreground) / 0.7)' }}>
                    Context: {currentContext}
                  </p>
                )}
              </div>
            )
          ) : (
            /* Browse mode */
            browseDocs.length > 0 ? (
              <div>
                {browseDocs.map((category) => (
                  <CategorySection
                    key={category.category}
                    category={category}
                    onSelectDoc={setSelectedDoc}
                  />
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 0', color: 'hsl(var(--muted-foreground))' }}>
                <p>No documentation available</p>
              </div>
            )
          )}
        </div>
      </div>

      <style>
        {`
          @keyframes help-spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          @keyframes help-panel-slide {
            from {
              transform: translateX(100%);
              opacity: 0.5;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
          @keyframes help-backdrop-fade {
            from { opacity: 0; }
            to { opacity: 1; }
          }
        `}
      </style>
    </>
  );
}
